<template>
  <div class="fly-box source-destination-test-connection-step">
    <div class="fly-box icon-container">
      <FlyIcon
        :class="'fly-icon ' + (isIconWarning ? 'icon-warning' : '')"
        :name="resultIcon"
      ></FlyIcon>
    </div>
    <div class="fly-box text-container">
      <div :class="'fly-text fly-text--ui-small ' + titleColor">
        {{ title }}
      </div>
      <div class="fly-text fly-text--ui-xsmall">{{ subTitle }}</div>
      <div
        v-if="setupStepVerifyQuery || setupStepFixQuery"
        class="setup-step-hint"
      >
        <div v-if="setupStepVerifyQuery" class="setup-step-query-section">
          <div class="fly-text fly-text--ui-xsmall setup-step-query-label">
            To verify, run:
          </div>
          <FlyCodeBlock lang="sql" :text="setupStepVerifyQuery" />
        </div>
        <div v-if="setupStepFixQuery" class="setup-step-query-section">
          <div class="fly-text fly-text--ui-xsmall setup-step-query-label">
            To fix, run:
          </div>
          <FlyCodeBlock lang="sql" :text="setupStepFixQuery" />
        </div>
      </div>
      <div v-if="errorMessage" class="fly-text fly-text--ui-xsmall">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import FlyIcon from 'shared/components/Icon/FlyIcon.vue';
import FlyCodeBlock from 'shared/components/Text/FlyCodeBlock';
import { get, isNil } from 'lodash';
import { mapGetters } from 'vuex';
import {
  getSnowflakeSetupErrorInfo,
  getSnowflakeValidationServerErrorInfo,
  getSnowflakeConnectionErrorInfo,
} from 'web/pipelines/snowflakeSetupQueries';
import { formFieldValue } from 'shared/stores/form/formGetterTypes';
import { CREATE_DESTINATION_FORM } from 'web/form/formKeys';

export default {
  name: 'SourceDestinationTestConnectionStep',
  components: {
    FlyIcon,
    FlyCodeBlock,
  },
  props: {
    stepResult: VueTypes.object.isRequired,
  },
  computed: {
    ...mapGetters({
      _formFieldValue: formFieldValue,
    }),
    connectionConfig() {
      return this._formFieldValue({
        key: CREATE_DESTINATION_FORM,
        field: 'config.connection_config',
      });
    },
    title() {
      if (!this.stepResult.success && !this.stepResult.mandatory) {
        return `${this.stepResult.action} (Optional)`;
      }
      return this.stepResult.action;
    },
    subTitle() {
      return this.stepResult.message;
    },
    snowflakeSetupInfo() {
      // Check backend error code first
      const code = get(this.stepResult, 'data.snowflake_setup_error_code');
      if (code) {
        return getSnowflakeSetupErrorInfo(code, this.connectionConfig);
      }
      if (!this.stepResult.success) {
        // Check connection error message patterns (e.g. role not found, role not granted)
        const connectionInfo = getSnowflakeConnectionErrorInfo(
          this.stepResult.message,
          this.connectionConfig,
        );
        if (connectionInfo) {
          return connectionInfo;
        }
        // Check Ghibli validation server action name for failed steps
        if (this.stepResult.action) {
          return getSnowflakeValidationServerErrorInfo(
            this.stepResult.action,
            this.connectionConfig,
            this.stepResult.data,
          );
        }
      }
      return null;
    },
    setupStepVerifyQuery() {
      return this.snowflakeSetupInfo ? this.snowflakeSetupInfo.verifyQuery : '';
    },
    setupStepFixQuery() {
      return this.snowflakeSetupInfo ? this.snowflakeSetupInfo.fixQuery : '';
    },
    errorMessage() {
      return get(this.stepResult, 'error.message', '');
    },
    resultIcon() {
      if (isNil(this.stepResult.success)) {
        return 'EmptyRadiobox';
      }
      if (this.stepResult.success) {
        return 'TickCircle';
      }
      return 'WarningCircle';
    },
    isIconWarning() {
      return !this.stepResult.success && !this.stepResult.mandatory;
    },
    titleColor() {
      if (isNil(this.stepResult.success)) {
        return 'grey-4';
      }
      if (this.stepResult.success) {
        return 'grey-1';
      }
      if (!this.stepResult.success && !this.stepResult.mandatory) {
        return 'grey-4';
      }
      return 'red-2';
    },
  },
};
</script>

<style lang="scss">
@import 'shared/styles/variables';
.source-destination-test-connection-step {
  margin: 8px 0;
  align-items: center;

  .icon-container {
    flex-shrink: 0;

    .fly-icon {
      margin-right: 16px;
      width: 16px;
      height: 16px;

      &.icon-warning {
        path {
          fill: $fly-color-yellow-3;
        }
      }
    }
  }

  .text-container {
    flex-direction: column;
    width: 100%;

    .fly-text--ui-xsmall {
      margin-top: 2px;
      color: $fly-color-grey-4;
    }

    .setup-step-hint {
      display: flex;
      margin-top: 8px;
      flex-direction: column;

      .setup-step-query-section {
        display: flex;
        margin-top: 8px;
        flex-direction: column;

        .setup-step-query-label {
          color: $fly-color-grey-3;
          font-size: 12px;
          margin-bottom: 0;
        }

        .fly-code-block-container {
          margin-top: 4px;
        }
      }
    }

    .fly-text--ui-small {
      &.grey-4 {
        color: $fly-color-grey-4;
      }
      &.grey-1 {
        color: $fly-color-grey-1;
      }
      &.red-2 {
        color: $fly-color-red-2;
      }
    }
  }
}
</style>
